import React, { useEffect, useState, useCallback } from "react";
import { Container, Row, Col, Card, Alert } from "react-bootstrap";
import { FaStar, FaStarHalfAlt } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import "../css/RatingByUser.css";

const RatingStars = ({ ratingValue }) => {
  const fullStars = Math.floor(ratingValue);
  const halfStar = ratingValue - fullStars >= 0.5;
  const emptyStars = 5 - fullStars - (halfStar ? 1 : 0);

  return (
    <div className="rating-stars d-flex mb-2">
      {Array.from({ length: fullStars }).map((_, i) => (
        <FaStar key={`full-${i}`} className="text-warning" />
      ))}
      {halfStar && <FaStarHalfAlt key="half" className="text-warning" />}
      {Array.from({ length: emptyStars }).map((_, i) => (
        <FaStar key={`empty-${i}`} className="text-muted" />
      ))}
    </div>
  );
};

const ShimmerCard = () => (
  <Col md={4} sm={6} className="mb-4">
    <Card className="shimmer-card">
      <div className="shimmer-img"></div>
      <Card.Body>
        <div className="shimmer-title"></div>
        <div className="shimmer-stars"></div>
        <div className="shimmer-text"></div>
        <div className="shimmer-text short"></div>
      </Card.Body>
    </Card>
  </Col>
);

function RatingByUser() {
  const { t } = useTranslation();
  const [ratedRooms, setRatedRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("token");

    axios
      .get(`${process.env.REACT_APP_APP_URL}/get-rating/byUser`, {
        headers: {
          "Content-Type": "application/json",
          "X-Platform": process.env.REACT_APP_X_PLATFORM,
          "API-Key": process.env.REACT_APP_API_KEY,
          "X-API-Version": process.env.REACT_APP_X_API_VERSION,
          Authorization: `Bearer ${token}`,
        },
        withCredentials: true,
      })
      .then((response) => {
        if (response.data.success) {
          setRatedRooms(response.data.data);
        } else {
          setError("Failed to fetch rated rooms.");
        }
      })
      .catch((error) => {
        console.error("Error fetching rated rooms:", error);
        setError("Error fetching data. Please try again.");
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const handleCardClick = useCallback(
    (roomTypeId) => {
      navigate(`/detail-room/${roomTypeId}`);
    },
    [navigate],
  );

  return (
    <Container className="rating-container">
      <h2 className="rating-title mb-4">My Rated Rooms</h2>
      {loading ? (
        <Row className="rating-grid">
          {[...Array(6)].map((_, i) => (
            <ShimmerCard key={i} />
          ))}
        </Row>
      ) : error ? (
        <Alert variant="danger" className="rating-alert">
          {error}
        </Alert>
      ) : ratedRooms.length === 0 ? (
        <Alert variant="info" className="rating-alert">
          You haven't rated any rooms yet.
        </Alert>
      ) : (
        <Row className="rating-grid">
          {ratedRooms.map((room) => {
            const roomType = room.room_type;
            const imageUrl =
              roomType?.photos?.length > 0
                ? `${process.env.REACT_APP_STORAGE_URL}/${roomType.photos[0].photo_url}`
                : "/assets/placeholder.jpg";

            return (
              <Col key={room.id} md={4} sm={6} className="mb-4">
                <Card
                  className="rating-card"
                  onClick={() => handleCardClick(roomType.id)}
                >
                  <div className="rating-img-container">
                    <Card.Img
                      variant="top"
                      src={imageUrl}
                      alt={roomType.name}
                    />
                  </div>
                  <Card.Body className="rating-card-body">
                    <Card.Title className="rating-card-title">
                      {roomType.name}
                    </Card.Title>
                    <RatingStars ratingValue={room.rating} />
                    <Card.Text className="rating-card-text">
                      <p>
                        <strong>Review:</strong> {room.review}
                      </p>
                      <p>
                        <strong>Capacity:</strong> {roomType.capacity} persons
                      </p>
                    </Card.Text>
                  </Card.Body>
                </Card>
              </Col>
            );
          })}
        </Row>
      )}
    </Container>
  );
}

export default RatingByUser;
